import React, { useState, useEffect } from 'react';
import { XMarkIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { ICategory } from '../../types/category.types';
import Button from '../common/Button';

interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { name: string; description?: string; color?: string }) => Promise<void>;
    category?: ICategory;
    title: string;
    workspaceId?: string;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    category,
    title,
    workspaceId
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3B82F6',
        workspace_id: workspaceId || ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name,
                description: category.description || '',
                color: category.color || '#3B82F6',
                workspace_id: workspaceId || ''
            });
        } else {
            setFormData({
                name: '',
                description: '',
                color: '#3B82F6',
                workspace_id: workspaceId || ''
            });
        }
    }, [category]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});

        if (!formData.name.trim()) {
            setErrors({ name: 'Vault name is required' });
            return;
        }

        try {
            setLoading(true);
            await onSubmit(formData);
            onClose();
        } catch (error) {
            console.error('Error submitting vault:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1000] overflow-y-auto">
            {/* Enhanced Backdrop - matching project theme */}
            <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"></div>
            
            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl w-full max-w-lg mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all">
                    
                    {/* Enhanced Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-6 py-4 sm:py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 sm:p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <BriefcaseIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-xl font-bold">{title}</h3>
                                    <p className="text-primary-100 text-xs sm:text-sm">
                                        {category ? 'Update vault details' : 'Create a new secure vault'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 sm:p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200"
                                aria-label="Close modal"
                            >
                                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-gray-50 dark:bg-gray-900 max-h-[calc(90vh-120px)] sm:max-h-[calc(95vh-140px)] overflow-y-auto">
                        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
                            {/* Vault Name Field */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Vault Name *
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={formData.name}
                                        maxLength={40}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className={`block w-full px-4 py-3 rounded-xl border-2 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20 transition-all duration-200 ${
                                            errors.name 
                                                ? 'border-red-300 dark:border-red-600 focus:border-red-500 dark:focus:border-red-400' 
                                                : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400'
                                        }`}
                                        placeholder="Enter vault name..."
                                    />
                                    <div className="absolute top-3 right-3 text-xs text-gray-400 dark:text-gray-500">
                                        {formData.name.length}/40
                                    </div>
                                </div>
                                {errors.name && (
                                    <div className="flex items-center space-x-2 text-red-600 dark:text-red-400 text-sm">
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <span>{errors.name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Description Field */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={4}
                                    className="block w-full px-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-200 resize-none"
                                    placeholder="Describe what you'll store in this vault..."
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Optional: Help team members understand the vault's purpose
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onClose}
                                    className="w-full sm:w-auto px-6 py-3 text-sm font-semibold"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full sm:w-auto px-6 py-3 text-sm font-semibold bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-primary-500/30"
                                >
                                    {loading ? (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            <span>Saving...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-2">
                                            <BriefcaseIcon className="h-4 w-4" />
                                            <span>{category ? 'Update Vault' : 'Create Vault'}</span>
                                        </div>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryModal;